import mitraAttendanceApi from '@/api/mitra-attendance/mitra-attendance';
import { normalizeBatchList } from '@/utils/batchIdentity';

const state = {
  courses: [],
  courseDetail: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPage: 0
  },
  loading: false,
  error: null,
  // Batch related state
  batches: [],
  availableParticipants: [], // Participants from API
  batchLoading: false,
  batchError: null,
  // Summary statistics
  summary: {
    this_year: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    },
    history: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    }
  },
  summaryLoading: false
};

const getters = {
  courses: state => state.courses || [],
  courseDetail: state => state.courseDetail || null,
  pagination: state => state.pagination || { total: 0, page: 1, limit: 10, totalPage: 0 },
  loading: state => state.loading || false,
  error: state => state.error || null,
  // Batch getters
  batches: state => state.batches || [],
  availableParticipants: state => state.availableParticipants || [],
  batchLoading: state => state.batchLoading || false,
  batchError: state => state.batchError || null,
  // Summary getters
  summary: state => state.summary || {
    this_year: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
    history: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
  },
  summaryLoading: state => state.summaryLoading || false
};

const mutations = {
  SET_COURSES(state, courses) {
    state.courses = courses;
  },
  SET_COURSE_DETAIL(state, courseDetail) {
    state.courseDetail = courseDetail;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total,
      page: pagination.page,
      limit: pagination.limit,
      totalPage: pagination.totalPage
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  // Batch mutations
  SET_BATCHES(state, batches) {
    state.batches = Array.isArray(batches) ? batches : [];
  },
  SET_AVAILABLE_PARTICIPANTS(state, participants) {
    state.availableParticipants = Array.isArray(participants) ? participants : [];
  },
  SET_BATCH_LOADING(state, loading) {
    state.batchLoading = loading;
  },
  SET_BATCH_ERROR(state, error) {
    state.batchError = error;
  },
  CLEAR_BATCH_ERROR(state) {
    state.batchError = null;
  },
  // Add participant to batch (local state)
  ADD_PARTICIPANT_TO_BATCH(state, { batchIndex, participant }) {
    if (state.batches[batchIndex]) {
      if (!state.batches[batchIndex].company_participants) {
        state.batches[batchIndex].company_participants = [];
      }
      state.batches[batchIndex].company_participants.push(participant);
    }
  },
  // Remove participant from batch (local state)
  REMOVE_PARTICIPANT_FROM_BATCH(state, { batchIndex, participantNik }) {
    if (state.batches[batchIndex]) {
      const index = state.batches[batchIndex].company_participants.findIndex(p => p.nik === participantNik);
      if (index !== -1) {
        // Get participant data before removing
        const removedParticipant = state.batches[batchIndex].company_participants[index];
        
        // Remove from batch
        state.batches[batchIndex].company_participants.splice(index, 1);
        
        // Add back to availableParticipants if not already there
        const existsInAvailable = state.availableParticipants.some(p => p.nik === participantNik);
        if (!existsInAvailable && removedParticipant) {
          state.availableParticipants.push(removedParticipant);
        }
      }
    }
  },
  // Update participant attendance status
  UPDATE_PARTICIPANT_ATTENDANCE(state, { batchIndex, participantNik, attendanceStatus }) {
    if (state.batches[batchIndex]) {
      const participant = state.batches[batchIndex].company_participants.find(p => p.nik === participantNik);
      if (participant) {
        participant.attendance_status = attendanceStatus;
      }
    }
  },
  // Summary mutations
  SET_SUMMARY(state, summary) {
    if (summary) {
      state.summary = {
        this_year: summary.this_year || { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
        history: summary.history || { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
      };
    }
  },
  SET_SUMMARY_LOADING(state, loading) {
    state.summaryLoading = loading;
  }
};

const actions = {
  async getCourses({ commit }, params) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraAttendanceApi.getCourses(params);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSES', response.data.data.data);
        commit('SET_PAGINATION', {
          total: response.data.data.total || 0,
          page: response.data.data.page || 1,
          limit: response.data.data.limit || 10,
          totalPage: response.data.data.totalPage || 0
        });
        return response.data.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch courses');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseDetail({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraAttendanceApi.getCourseDetail(id);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSE_DETAIL', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch course detail');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get batches with participants
  async getBatches({ commit }, courseId) {
    commit('SET_BATCH_LOADING', true);
    commit('CLEAR_BATCH_ERROR');
    
    try {
      const response = await mitraAttendanceApi.getBatches(courseId);
      
      if (response.data.httpCode === 200) {
        commit('SET_BATCHES', normalizeBatchList(response.data.data));
        commit('SET_AVAILABLE_PARTICIPANTS', []);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch batches');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_BATCH_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_BATCH_LOADING', false);
    }
  },

  // Submit attendance (final submit)
  async submitAttendance({ commit }, { batchId, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraAttendanceApi.submitAttendance(batchId, data);
      
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to submit attendance');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get summary statistics
  async getSummary({ commit }, params) {
    commit('SET_SUMMARY_LOADING', true);
    
    try {
      const response = await mitraAttendanceApi.getSummary(params);
      
      if (response.data.httpCode === 200) {
        commit('SET_SUMMARY', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch summary');
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    } finally {
      commit('SET_SUMMARY_LOADING', false);
    }
  },

  // Local state management
  addParticipantToBatch({ commit }, payload) {
    commit('ADD_PARTICIPANT_TO_BATCH', payload);
  },

  removeParticipantFromBatch({ commit }, payload) {
    commit('REMOVE_PARTICIPANT_FROM_BATCH', payload);
  },

  updateParticipantAttendance({ commit }, payload) {
    commit('UPDATE_PARTICIPANT_ATTENDANCE', payload);
  },

  // Download attendance template
  async downloadAttendanceTemplate(_, courseId) {
    try {
      const response = await mitraAttendanceApi.downloadTemplate(courseId);
      return response;
    } catch (error) {
      console.error('Error downloading template:', error);
      throw error;
    }
  },

  // Bulk upload attendance
  async bulkUploadAttendance(_, { courseId, formData }) {
    try {
      const response = await mitraAttendanceApi.uploadAttendance(courseId, formData);
      return response.data;
    } catch (error) {
      console.error('Error uploading attendance:', error);
      throw error;
    }
  },

  // Generate signed URL for QR code
  async generateSignedUrl(_, scheduleId) {
    try {
      const response = await mitraAttendanceApi.generateSignedUrl(scheduleId);
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to generate signed URL');
      }
    } catch (error) {
      console.error('Error generating signed URL:', error);
      throw error;
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
